import type { PluginOption, ResolvedConfig } from 'vite';
import MagicString from 'magic-string';
import { init, parse } from 'es-module-lexer';
import {
  cleanUrl,
  isViteOptimized,
  shouldOptimizeDependency as originalShouldOptimizeDependency,
} from './utils.js';
import { optimizeCjsDependency } from './optimizeCjsDependency.js';
import { interopNamedImports } from './interopNamedImports.js';

export function optimizeBackendExtensions({
  projectFolder,
  exclude = [],
}: {
  projectFolder: string;
  exclude?: string[];
}): PluginOption {
  let config: ResolvedConfig | undefined;

  const cache = new Map<string, boolean>();

  const shouldOptimizeDependency = async (url: string) => {
    const cachedResult = cache.get(url);

    if (cachedResult) {
      return cachedResult;
    }

    const result = await originalShouldOptimizeDependency(url);
    cache.set(url, result);

    return result;
  };

  return {
    name: 'wix:optimize-backend-deps',
    apply: 'serve',
    configResolved(resolvedConfig) {
      config = resolvedConfig;
    },
    async transform(code, id) {
      const url = cleanUrl(id);

      if (isViteOptimized(url, projectFolder) || exclude.includes(url)) {
        return null;
      }

      if (await shouldOptimizeDependency(url)) {
        return optimizeCjsDependency({ url, projectFolder });
      }

      await init;

      const [imports] = parse(code);
      const str = new MagicString(code);

      for (const [index, importSpecifier] of imports.entries()) {
        if (!importSpecifier.n) {
          continue;
        }

        const resolved = await this.resolve(importSpecifier.n, id);

        if (resolved) {
          const url = cleanUrl(resolved.id);

          if (!(await shouldOptimizeDependency(url))) {
            continue;
          }

          if (isViteOptimized(url, projectFolder)) {
            continue;
          }

          interopNamedImports(str, importSpecifier, url, index, id, config!);
        }
      }

      return str.toString();
    },
  };
}
